import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquareCheck, faSquare } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import { Button } from "frontend-components";
import type { WidgetConfig } from "../../utils/dashboard.types";

interface WidgetOverviewBarProps {
    id: string;
    isOpen: boolean;
    allWidgets: WidgetConfig[];
    activeWidgetIds: string[];
    onToggleWidget: (widgetId: string) => void;
    onResetLayout: () => void;
}

export function WidgetOverviewBar({
    id,
    isOpen,
    allWidgets,
    activeWidgetIds,
    onToggleWidget,
    onResetLayout,
}: WidgetOverviewBarProps) {
    const { t } = useTranslation();

    return (
        <div
            id={id}
            aria-hidden={!isOpen}
            inert={!isOpen}
            className={`grid transition-[grid-template-rows] duration-250 ease-out ${
                isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
            }`}
        >
            <div className="overflow-hidden">
                <div className="flex flex-wrap items-center gap-2 pt-4 pb-3">
                    <span className="text-category uppercase tracking-category text-compendia-muted">
                        {t("dashboard.showWidgets")}:
                    </span>
                    {allWidgets.map((widget) => {
                        const isActive = activeWidgetIds.includes(widget.id);
                        return (
                            <button
                                key={widget.id}
                                type="button"
                                onClick={() => onToggleWidget(widget.id)}
                                aria-pressed={isActive}
                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-body-sm transition-colors ${
                                    isActive
                                        ? "bg-chameleon-green-10 border-chameleon-green text-compendia-foreground"
                                        : "bg-transparent border-compendia-foreground text-compendia-foreground hover:bg-compendia-foreground/5"
                                }`}
                            >
                                <FontAwesomeIcon
                                    icon={isActive ? faSquareCheck : faSquare}
                                    className={
                                        isActive ? "text-chameleon-green" : "text-compendia-muted"
                                    }
                                />
                                {t(widget.name)}
                            </button>
                        );
                    })}
                    <Button
                        type="button"
                        variant="ghost"
                        size="default"
                        onClick={onResetLayout}
                        className="ml-auto"
                    >
                        {t("dashboard.resetLayout")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
